import { Component, Input, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { ModalController, NavParams } from '@ionic/angular';
import { Finishing, ShippingType } from 'src/app/shared-lib/core/settings';
import { Coupon, CouponService } from 'src/app/shared-lib/domains/coupon';
import { OrderTypeStatus, TrackingData } from 'src/app/shared-lib/domains/order';
import { OrderPaymentStatus, PaymentType } from 'src/app/shared-lib/domains/payment';
import { ProductType, sortItemsByProductType } from 'src/app/shared-lib/domains/product';
import { ShippingLabelService } from 'src/app/shared-lib/domains/shipping-label';
import { UserService } from 'src/app/shared-lib/domains/user';
import { DiscountType } from 'src/app/shared-lib/global/models';
import { AppService } from 'src/app/shared/app.service';
import { CartService } from 'src/app/shared/cart.service';
import { OrderStateService } from 'src/app/shared/order-state.service';
import { OrderService } from 'src/app/shared/order.service';
import { ShareService } from 'src/app/shared/share.service';
import { downloadInvoice, OrderProcessStatus } from '../../shared-lib/domains/order';

@Component({
  selector: 'app-order-detail',
  templateUrl: './order-detail.component.html',
  styleUrls: ['./order-detail.component.scss'],
  standalone: false
})
export class OrderDetailComponent implements OnInit {

  @Input() trackingData: TrackingData;

  //Enums
  processStatus = OrderProcessStatus;
  paymentStatus = OrderPaymentStatus;
  paymentType = PaymentType;
  orderTypeStatus: typeof OrderTypeStatus = OrderTypeStatus;
  productType = ProductType;
  discountType = DiscountType;
  finishing = Finishing;
  shippingType = ShippingType;

  //Val
  id: string = null;

  //Obs
  order;

  shippingTypeLabel = {
    standard: {
      svg: '../../../assets/icon/camion.svg',
      text: 'ESTÁNDAR'
    },
    standard_24: {
      svg: '../../../assets/icon/avion.svg',
      text: 'PRIORITARIO'
    },
    urgent: {
      svg: '../../../assets/icon/cohete.svg',
      text: 'URGENTE'
    }
  };

  paymentLabel = {
    transfer: {
      svg: '../../../assets/icon/transferenciaBancaria.svg',
      text: 'Transferencia'
    },
    bizum: {
      svg: '../../../assets/icon/logoBizum.svg',
      text: 'Bizum'
    },
    creditcard: {
      svg: '../../../assets/icon/tarjetaCredito.svg',
      text: 'Tarjeta de crédito/débito'
    },
    fullbonus: {
      svg: '../../../assets/icon/bonoMonedero.svg',
      text: 'Bono monedero'
    }
  };

  coupon: Coupon = null;

  constructor(
    private modalCtrl: ModalController,
    private navParams: NavParams,
    private orderService: OrderService,
    private shareService: ShareService,
    private shippingLabelService: ShippingLabelService,
    private orderStateService: OrderStateService,
    private userService: UserService,
    private router: Router,
    public cartService: CartService,
    public couponService: CouponService,
    protected appS: AppService
  ) {
    const { info } = navParams.data;
    if (!info.order) {
      this.id = info.id;
      this.order = null;
    } else {
      this.order = info.order;
      this.id = info.order.id;
    }
  }

  ngOnInit() {
    this.loadOrder();

    let orderCoupon = this.order?.cart.discounts.find(discount => discount.type === 'coupon');

    if (orderCoupon) {
      this.couponService.getCouponByCode$(orderCoupon.refId).subscribe(coupon => this.coupon = coupon);

    }

  }

  private loadOrder() {
    if (!this.order) {
      this.orderService.get$(this.id).subscribe(o => this.order = o);
    }
  }

  orderItems(items: Array<any>): Array<any> {
    return sortItemsByProductType(items);
  }

  onCloseModal() {
    this.modalCtrl.dismiss();
  }

  // traduce como en admin el tipo de pago
  paymentMethodES(paymentType): any {
    const paymentTypeES = {
      TRANSFER: 'Transferencia Bancaria',
      BIZUM: 'Bizum',
      CREDITCARD: 'Tarjeta de Crédito',
      MONTHLYPAYMENT: 'Pago Mensual / Remesa',
      FULLBONUS: 'Descuento Completo Bono/Cupón'
    };
    let newType = paymentTypeES[paymentType?.toUpperCase()];
    return newType;
  }

  share() {
    // console.log("ID: ", this.id);
    this.shareService.shareOrder(this.id);
  }

  onDeleteDraft(orderDraft) {
    this.userService.deleteUserDraftOrder(this.userService.userG?.id, orderDraft.id);
  }

  onLoadOrder(order) {
    this.orderStateService.loadOrder(order);
    this.router.navigate(['/cart']);
  }

  get isPaid() {
    return this.order.paymentStatus == OrderPaymentStatus.PAID;
  }

  get isPauseButtonDisplayed() {
    return [
      OrderProcessStatus.PENDING, // now, pending only
      // OrderProcessStatus.PROCESSING,
      // OrderProcessStatus.COMPLETE,
      // OrderProcessStatus.SHIPPED
    ].includes(this.order.processStatus) && !this.order.cart.items.some(it => it.product.presale);
  }

  get isProcessPending() {
    return this.order.processStatus == OrderProcessStatus.PENDING;
  }

  printGroupItems(items) {
    return items.filter(
      (item) => item.product.type === ProductType.PRINT && !item.product.vendor
    );
  }

  existStationeryProducts(items) {
    return !!items?.find(
      (item) =>
        item.product.type === this.productType.STORE ||
        item.product.type === this.productType.WALLETBONUS ||
        item.product.type === this.productType.GENERIC
    );
  }

  keepMinZero(value: number) {
    return Math.max(0, value);
  }

  getBasePrice() {
    let walletDiscount = this.order.cart.discounts.find(dis => dis.type == 'wallet')?.amount ?? 0;
    return (
      this.order.cart.price.totalAmt -
      walletDiscount -
      this.order.cart.shipping.price.amt -
      (this.coupon
        ? this.getDiscount(this.discountType.COUPON)
        : 0)
    );
  }

  getAfterPrice() {
    let afterPrice = this.getBasePrice() + this.getDiscount(this.discountType.COUPON);

    if (this.coupon && this.coupon?.freeShipping) {
      const { amt } = this.cartService.getShippingPrice(this.order.shippingAddress?.postalCode, this.order.cart, ShippingType.STANDARD);
      afterPrice += amt;
    }
    return afterPrice;
  }

  getDiscount(type?) {
    return this.order.cart.discounts.reduce((prev, discount) => {
      if (type) {
        return (discount.type == type) ? prev + discount.amount : prev;
      } else {
        return prev + discount.amount;
      }
    }, 0);
  }

  isOnlyFreeShipping() {
    if (!this.coupon) {
      return false;
    }

    const shippingAmt = this.cartService.getShippingPrice(this.order.shippingAddress?.postalCode, this.order.cart, ShippingType.STANDARD).amt;
    return this.coupon?.freeShipping && shippingAmt + this.getDiscount(this.discountType.COUPON) === 0;
  }

  onDownloadInvoice() {
    downloadInvoice(this.order);
  }

}

